import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Portal from '../Portal';
import { useTracking } from '../../hooks/useTracking';
import { useUser } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';

interface InsufficientCreditsModalProps {
  current_credits: number;
  required_credits: number;
  message: string;
  onClose: () => void;
}

export function InsufficientCreditsModal({
  current_credits,
  required_credits,
  message,
  onClose,
}: InsufficientCreditsModalProps) {
  const navigate = useNavigate();
  const { track } = useTracking();
  const { user: userData } = useUser();
  const { user } = useAuth();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  // Check if user has active subscription
  const hasActiveSubscription = userData?.subscription_status === 'active' || userData?.subscription_status === 'canceling';

  // Lock body scroll when modal is open and track modal display
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Track modal display event
    const sourcePage = window.location.pathname;
    const creditsDeficit = required_credits - current_credits;
    
    track('insufficient_credits_modal_shown', {
      source_page: sourcePage,
      current_credits: current_credits,
      required_credits: required_credits,
      credits_deficit: creditsDeficit,
      timestamp: Date.now(),
    });
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [track, current_credits, required_credits]);

  // Open Stripe Customer Portal (no email login needed!)
  const openStripePortal = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      console.error('❌ [InsufficientCreditsModal] No user ID available');
      alert('Unable to open billing portal. Please try refreshing the page.');
      return;
    }

    if (isLoadingPortal) {
      return; // Prevent multiple clicks
    }

    try {
      setIsLoadingPortal(true);
      
      // Create portal session via API
      const { url } = await apiClient.createPortalSession(
        user.id,
        `${window.location.origin}/my-collection`
      );
      
      // Redirect to Stripe portal (no email needed!)
      window.location.href = url;
    } catch (error) {
      console.error('❌ [InsufficientCreditsModal] Failed to open billing portal:', error);
      
      // Close overlay first
      setIsLoadingPortal(false);
      
      // Show error after a brief delay to ensure overlay closes
      setTimeout(() => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to open billing portal. Please try again.';
        alert(`${errorMessage}\n\nNote: If you're on dev environment, make sure you have a test subscription created with dev's test Stripe keys.`);
      }, 100);
    }
  };

  const handleUpgrade = async (e: React.MouseEvent) => {
    if (hasActiveSubscription) {
      // User has subscription → open Stripe portal
      await openStripePortal(e);
    } else {
      // User has no subscription → navigate to upgrade page
      const sourcePage = window.location.pathname;
      const creditsDeficit = required_credits - current_credits;
      
      track('insufficient_credits_upgrade_clicked', {
        source_page: sourcePage,
        current_credits: current_credits,
        required_credits: required_credits,
        credits_deficit: creditsDeficit,
        timestamp: Date.now(),
      });
      
      onClose();
      navigate('/upgrade');
    }
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <div className="bg-bg border border-white/20 rounded-xl p-6 sm:p-8 w-full max-w-md shadow-lg">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white text-center">Insufficient Credits</h2>
          </div>

          <div className="mb-8">
            <p className="text-white/90 text-lg text-center">
              You don't have enough credits to generate a video.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Current credits:</span>
              <span className="text-white font-semibold">{current_credits}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Required:</span>
              <span className="text-white font-semibold">{required_credits}</span>
            </div>
          </div>

          <button
            onClick={handleUpgrade}
            disabled={isLoadingPortal}
            className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 min-h-[48px] rounded-full text-base font-semibold hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingPortal ? 'Opening billing portal...' : 'Upgrade'}
          </button>
        </div>
      </div>
    </Portal>
  );
}

